import {
  Alert,
  Backdrop,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { AppReviewRepository } from "../repositories/AppReviewRepository";
import { useDispatch } from "react-redux";
import { AuthRepository } from "../repositories/AuthRepository";
import {
  notifyShowErrorMessage,
  notifyShowSuccessMessage,
} from "../../store/AlertSlice";
import PdfViewer from "../entries/PdfViewer";

export const ReviewDecisionSigning = ({
  open,
  setOpen,
  appReview,
  setAppReview,
}) => {
  const [saving, setSaving] = useState(false);
  const [generatedDocument, setGeneratedDocument] = useState(null);
  const [keystorePassword, setKeystorePassword] = useState("");
  const [openKeystoreDialog, setOpenKeystoreDialog] = useState(false);
  const [signingExtensionLoader, setSigningExtensionLoader] = useState(false);
  const [openSigningExtension, setOpenSigningExtension] = useState(false);
  const [isSignerInstalled, setIsSignerInstalled] = useState(false);
  const [returnToCorrectionDialogOpen, setReturnToCorrectionDialogOpen] =
    useState();
  const [correctionComment, setCorrectionComment] = useState();
  const [uploadedSignedDocument, setUploadedSignedDocument] = useState(null);
  const uploadedSignedDocumentRef = React.useRef(null);

  const dispatch = useDispatch();

  useEffect(() => {
    if (appReview && open) {
      loadGeneratedDocument();
    }
  }, [open, appReview]);

  useEffect(() => {
    setSigningExtensionLoader(true);
    const maxWaitTime = 5000;
    const interval = setInterval(() => {
      if (window.AuctaSignerApp) {
        clearInterval(interval);
        window.AuctaSignerApp.isInstalled((status) => {
          console.log("[external page] AuctaSignerApp.isInstalled ->", status);
          if (status === "OK") {
            console.log("AuctaSigner extension is installed.");
            setIsSignerInstalled(true);
            setSigningExtensionLoader(false);
          } else {
            console.log("AuctaSigner extension is not installed.");
            setIsSignerInstalled(false);
            setSigningExtensionLoader(false);
          }
        });
      }
    }, 200);

    const timeout = setTimeout(() => {
      clearInterval(interval);
      if (!window.AuctaSignerApp) {
        console.log("AuctaSigner extension is not installed.");
      }
      setSigningExtensionLoader(false);
    }, maxWaitTime);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    const handler = (event) => {
      if (event.source !== window) return;

      const msg = event.data;
      if (msg && typeof msg.data === "string") {
        const base64 = msg.data
          .replace(/-/g, "+")
          .replace(/_/g, "/")
          .replace(/\s+/g, "");

        try {
          const decodedJsonString = atob(base64);
          const parsed = JSON.parse(decodedJsonString);
          const pdfBase64 = parsed.message;

          const byteCharacters = atob(pdfBase64);
          const byteNumbers = new Array(byteCharacters.length)
            .fill(0)
            .map((_, i) => byteCharacters.charCodeAt(i));
          const byteArray = new Uint8Array(byteNumbers);
          const pdfBlob = new Blob([byteArray], {
            type: "application/pdf",
          });
          uploadSignedDocument(pdfBlob);
          window.postMessage(
            {
              type: "CLOSE_POPUP",
            },
            "*",
          );
        } catch (err) {
          console.error("Failed to decode or parse the message:", err);
        }
      } else {
        console.log("Unexpected message format:", msg);
      }
    };

    window.addEventListener("message", handler);
    return () => {
      window.removeEventListener("message", handler);
    };
  }, []);

  const handleClose = () => {
    setSaving(true);
    AppReviewRepository.getById(appReview.id)
      .then((res) => {
        setAppReview(res.data);
        setSaving(false);
        setOpen(false);
      })
      .catch((err) => {
        console.error(err);
        setSaving(false);
        dispatch(
          notifyShowErrorMessage(
            "Грешка при освежување на податоците за ревизијата.",
          ),
        );
      });
  };

  const loadGeneratedDocument = () => {
    setSaving(true);
    AppReviewRepository.getDecisionDocumentForSigning(appReview?.id)
      .then((res) => {
        setGeneratedDocument(res.data);
      })
      .catch((err) => {})
      .finally(() => {
        setSaving(false);
      });
  };

  const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        // result looks like: data:application/pdf;base64,JVBERi0x...
        const base64 = reader.result.split(",")[1]; // remove metadata
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const signAndUploadDocumentWithToken = () => {
    blobToBase64(generatedDocument).then((base64Pdf) => {
      window.postMessage(
        {
          type: "FILE_UPLOAD",
          action: "WITH_PREVIEW",
          subaction: "SILENT_SIGN_PDF",
          fileName: "decision.pdf",
          fileType: "application/pdf",
          fileContent: base64Pdf,
        },
        "*",
      );
    });
  };

  const uploadSignedDocument = (signedPdfBlob) => {
    setSaving(true);
    const file = new File([signedPdfBlob], "decision.pdf", {
      type: "application/pdf",
    });
    const formData = new FormData();
    formData.append("appReviewId", appReview.id);
    formData.append("decisionDocument", file);
    formData.append("approved", appReview.isApproved);
    AppReviewRepository.uploadSignedInstitutionDecision(formData)
      .then((res) => {
        dispatch(
          notifyShowSuccessMessage("Одлуката е успешно потпишана и испратена."),
        );
        handleClose();
      })
      .catch((err) => {
        setSaving(false);
        dispatch(
          notifyShowErrorMessage("Грешка при испраќање на потпишаната одлука."),
        );
        console.error("Error uploading signed document:", err);
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const signAndUploadDocument = () => {
    setSaving(true);
    const file = new File([generatedDocument], "decision.pdf", {
      type: "application/pdf",
    });
    const formData = new FormData();
    formData.append("appReviewId", appReview.id);
    formData.append("decisionDocument", file);
    formData.append("keystorePassword", keystorePassword);
    formData.append("approved", appReview.isApproved);

    AppReviewRepository.signAndUploadInstitutionDecision(formData)
      .then((res) => {
        dispatch(
          notifyShowSuccessMessage("Одлуката е успешно потпишана и испратена."),
        );
        handleClose();
      })
      .catch((err) => {
        setSaving(false);
        dispatch(
          notifyShowErrorMessage(
            "Грешка при потпишување и испраќање на одлуката.",
          ),
        );
        console.error("Error signing and uploading document:", err);
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const handleReturnToCorrection = () => {
    setSaving(true);
    AppReviewRepository.returnForCorrection(appReview?.id, correctionComment)
      .then((res) => {
        dispatch(
          notifyShowSuccessMessage(
            "Предметот НЕ Е ПОТПИШАН и е успешно вратен на корекција",
          ),
        );
        window.location.reload();
      })
      .catch((err) => {
        setSaving(false);
        dispatch(notifyShowErrorMessage("Грешка при враќање на корекција."));
      })
      .finally(() => {
        setSaving(false);
      });
  };

  return (
    <>
      <Dialog open={open} fullWidth maxWidth="md">
        <DialogTitle>
          {appReview.isApproved
            ? "Потпишување на позитивна одлука"
            : "Потпишување на негативна одлука"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            {generatedDocument && <PdfViewer pdfBlob={generatedDocument} />}
          </Grid>
        </DialogContent>
        <DialogActions>
          {AuthRepository.hasAnyRole(["ROLE_JPDP_SIGNER"]) && (
            <>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  uploadedSignedDocumentRef.current.click();
                }}
              >
                Прикачи потпишана одлука (надворешен потпис)
              </Button>
              <input
                type="file"
                ref={uploadedSignedDocumentRef}
                style={{ display: "none" }}
                accept="application/pdf"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = (event) => {
                      const signedPdfBlob = new Blob([event.target.result], {
                        type: "application/pdf",
                      });
                      uploadSignedDocument(signedPdfBlob);
                    };
                    reader.readAsArrayBuffer(file);
                  }
                }}
              />
            </>
          )}
          {generatedDocument ? (
            <Button
              variant="contained"
              color="success"
              onClick={() => {
                setOpenSigningExtension(true);
              }}
            >
              Потпиши одлука со токен
            </Button>
          ) : null}
          {generatedDocument ? (
            <Button
              variant="contained"
              color="success"
              onClick={() => {
                setKeystorePassword("");
                setOpenKeystoreDialog(true);
              }}
            >
              Потпиши одлука со снимен потпис
            </Button>
          ) : null}

          <Button
            variant="contained"
            color="error"
            onClick={() => {
              setReturnToCorrectionDialogOpen(true);
            }}
          >
            Врати на корекција
          </Button>

          <Button
            variant="outlined"
            color="error"
            onClick={() => {
              setOpen(false);
            }}
          >
            Откажи
          </Button>
        </DialogActions>
      </Dialog>
      {openKeystoreDialog && (
        <Dialog
          open={openKeystoreDialog}
          onClose={() => setOpenKeystoreDialog(false)}
        >
          <DialogTitle>Внесете лозинка за дигитален потпис</DialogTitle>
          <DialogContent>
            <TextField
              value={keystorePassword}
              onChange={(e) => setKeystorePassword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  setOpenKeystoreDialog(false);
                  signAndUploadDocument();
                }
              }}
              label="Лозинка"
              type="password"
              fullWidth
              margin="normal"
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setOpenKeystoreDialog(false);
              }}
            >
              Откажи
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setOpenKeystoreDialog(false);
                signAndUploadDocument();
              }}
            >
              Потврди
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {saving && (
        <Backdrop open={true} style={{ zIndex: 9999, color: "#fff" }}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      {openSigningExtension && (
        <Dialog
          open={openSigningExtension}
          onClose={() => setOpenSigningExtension(false)}
        >
          <DialogContent>
            {!isSignerInstalled && !signingExtensionLoader && (
              <Alert
                severity="error"
                variant="filled"
                sx={{ padding: "4px 8px" }}
              >
                За потпишување на документите, потребно е да ја инсталирате
                екстензијата за потпишување.{" "}
                <a
                  target="_blank"
                  style={{ color: "white", fontWeight: "bold" }}
                  href="https://chromewebstore.google.com/detail/aucta-signer-v2/gdekohcdoabefpfpjcanibkkhfinifmn"
                >
                  Преземете ја екстензијата тука
                </a>
                <br />
                По инсталирањето на екстензијата освежете ја страницата{" "}
                <a
                  href="#"
                  style={{ color: "white", fontWeight: "bold" }}
                  onClick={() => window.location.reload()}
                >
                  тука
                </a>
                .
              </Alert>
            )}
            {isSignerInstalled && !signingExtensionLoader && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Alert
                    severity="info"
                    variant="filled"
                    sx={{ padding: "4px 8px" }}
                  >
                    Освен екстензијата, потребно е да имате инсталирано и
                    помошна апликација за комуникација со екстензијата. Истата
                    може да ја преземете тука:{" "}
                    <a
                      style={{ color: "white", fontWeight: "bold" }}
                      href="/install/aucta-signer-native-v2-installer.msi"
                      download
                    >
                      aucta-signer-native-v2-installer.msi
                    </a>
                  </Alert>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    onClick={() => {
                      signAndUploadDocumentWithToken(generatedDocument);
                    }}
                  >
                    Потпиши и испрати
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <Typography>
                    <PdfViewer pdfBlob={generatedDocument} />
                  </Typography>
                </Grid>
              </Grid>
            )}
          </DialogContent>
        </Dialog>
      )}
      {returnToCorrectionDialogOpen && (
        <Dialog
          fullWidth
          maxWidth="sm"
          open={returnToCorrectionDialogOpen}
          onClose={() => setReturnToCorrectionDialogOpen()}
        >
          <DialogTitle>Врати на корекција</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} mt={0.5}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Коментар за корекција"
                  placeholder="Коментар за корекција"
                  value={correctionComment}
                  rows={3}
                  multiline={true}
                  onChange={(e) => {
                    setCorrectionComment(e.target.value);
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              color="error"
              variant="outlined"
              onClick={() => {
                setReturnToCorrectionDialogOpen();
              }}
            >
              Откажи
            </Button>
            <Button
              color="primary"
              variant="contained"
              onClick={() => handleReturnToCorrection()}
            >
              Поднеси
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};
